import React, {useMemo, useState} from "react";
import {
	Alert,
	Avatar,
	Box, Button,
	Card,
	CardContent,
	CardHeader, Container, Dialog, DialogContent, DialogTitle,
	Link, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow, Typography
} from "@mui/material";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import {useLocalization} from "../../i18n";
import {useBooking} from "../../context/booking";
import {DisplayCurrency, isBlank, notBlank} from "../../utils";
import {
	BookingCancelHint,
	BookingExpirationCountDown,
	BookingProlongationButton,
	BoxesAccess, PendingPaymentButton
} from "./booking-panels";
import {TemporalFormat, TemporalRangeFormat, ZonedDateTime} from "@variocube/app-ui";
import {BookingBoxes} from "../../data/bookings";
import {B2Booking, B2BookingPayment} from "../../data/client.gen";
import {green} from "@mui/material/colors";
import {Accessibility} from "../../component/accessibility";
import {BookingHeader} from "./booking-header";
import {parseZoneDateTimeFromInstant} from "../../dates-utils";

export function BookingDetails() {
	const {t, language} = useLocalization();
	const {booking, isBookingActive} = useBooking();
	const zoneId = booking?.site.zoneId ?? 'UTC';

	/**
	 * Pending booking is any bookings that has:
	 * - amount > 0
	 * - not yet paid
	 * - state equals to Pending
	 *
	 * This type of booking will be automatically canceled after a certain amount of time.
	 * Use this flag to toggle the warning alert on the UI.
	 */
	const isPendingBooking = useMemo(() =>
		notBlank(booking?.paymentId) && booking?.state === 'Pending' && booking.paymentStatus !== 'paid'
		, [booking]);

	/**
	 * Pending payment is any bookings that:
	 * - is not in pending state
	 * - has payment status as open
	 */
	const isPendingPayment = useMemo(() =>
		!isPendingBooking && booking?.paymentStatus === 'open'
		, [isPendingBooking, booking])

	/**
	 * Canceled booking is any bookings that has:
	 * - inactive state
	 * - state equals to Canceled
	 *
	 * Use this flag to toggle the booking was canceled hint.
	 */
	const isBookingCanceled = useMemo(() =>
		!isBookingActive && booking?.state === 'Canceled'
		, [isBookingActive, booking]);

	/**
	 * Booking with active boxes has:
	 * - active state
	 * - site external access is None
	 * - payment status equals to paid
	 *
	 * Use this flag to toggle access to booking boxes.
	 */
	const isBoxesActive = useMemo(() =>
		booking !== undefined && isBookingActive && !booking.site.externalAccess && booking.paymentStatus === 'paid'
		, [isBookingActive, booking])

	/**
	 * Prolongeable booking is any bookings that has:
	 * - site external access is None
	 * - at least 1 occupancy
	 * - not yet prolonged
	 */
	const isBookingProlongeable = useMemo(() =>
		booking !== undefined && !isBookingActive && !booking.site.externalAccess && (booking.occupancies ?? []).length > 0 && isBlank(booking.prolongUntil)
		, [isBookingActive, booking]);

	return (
		<Container maxWidth="md">
			<Card sx={{ mt: 2 }}>
				<CardHeader
					title={<BookingHeader />}
				/>
				{booking !== undefined && (
					<>
						{isPendingBooking && <BookingExpirationCountDown booking={booking} />}
						{isPendingPayment && <PendingPaymentButton booking={booking} />}
						{isBoxesActive && <BoxesAccess booking={booking} />}
						{isBookingCanceled && <BookingCancelHint />}
						{isBookingProlongeable && <BookingProlongationButton booking={booking} />}

						<TableContainer component={CardContent}>
							<Table>
								<TableBody>
									<BookingRows booking={booking} />
									<TableRow>
										<TableCell component="th" scope="row">
											{t('dates.period')}:
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2">
												<TemporalRangeFormat
													locale={language}
													from={parseZoneDateTimeFromInstant(booking.from, zoneId)}
													until={parseZoneDateTimeFromInstant(booking.until, zoneId)}
												/>
												{/*&nbsp;*/}
												{/*{booking.timezone}*/}
											</Typography>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell component="th" scope="row">
											{t('boxes.title')}:
										</TableCell>
										<TableCell align="right">
											{Object.entries(booking.boxes as BookingBoxes)
												.map( ([size, quantity]) => (
													<div key={size}>{size}: {quantity}</div>
												))
											}
										</TableCell>
									</TableRow>
									{booking.payments !== undefined && booking.payments.length > 0 && (
										<TableRow>
											<TableCell component="th" scope="row" colSpan={2}>
												{t('payment.title')}
												<Box my={1} />

												<BookingPaymentList
													payments={booking.payments}
													currency={booking.site.currency}
													zoneId={zoneId}
												/>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</Card>
			<Box my={3} />
			{notBlank(booking?.site.terms) && (
				<Typography textAlign="center" gutterBottom><Link href={booking?.site.terms} target="_blank">{t('sites.terms')}</Link></Typography>
			)}
			{notBlank(booking?.site.privacyPolicy) && (
				<Typography textAlign="center" gutterBottom><Link href={booking?.site.privacyPolicy} target="_blank">{t('sites.privacyPolicy')}</Link></Typography>
			)}
		</Container>
	)
}

function BookingRows({booking}: { booking: B2Booking }) {
	const {t} = useLocalization();
	const {site, paymentId, paymentStatus, email, name, phone} = booking;
	return (
		<>
			{[
				{
					label: t('booking.site'),
					value: (
						<>
							<Typography><strong>{site.name}</strong></Typography>
							<Typography variant="body2">{site.street}</Typography>
							<Typography variant="body2">{site.city} {site.country}</Typography>
							{notBlank(site.accessDescription) && (
								<Alert severity="info" sx={{ mt: 2 }}>{site.accessDescription}</Alert>
							)}
						</>
					)
				},
				{
					label: t('sites.accessibility'),
					value: <Accessibility site={site} />
				},
				...(notBlank(paymentId)
						? [{
							label: t('payment.status'),
							value: <span>{paymentStatus?.toUpperCase()}</span>
						}]
						: []
				),
				...([
						{
							label: t('customer.email'),
							value: email
						},
						{
							label: t('customer.name'),
							value: name
						},
						{
							label: t('customer.phone'),
							value: phone
						},
					]
						.filter(({value}) => notBlank(value))
				)
			]
				.map(({ label, value }) => (
					<TableRow key={label}>
						<TableCell
							component="th"
							scope="row"
						>
							{label}:
						</TableCell>
						<TableCell align="right">{value}</TableCell>
					</TableRow>
				))}
		</>
	)

}

function BookingPaymentList({payments, zoneId, currency}: { payments: Array<B2BookingPayment>, zoneId: string, currency: string|undefined }) {
	const {t, language} = useLocalization();

	const [receipt, setReceipt] = useState<string>();

	return (
		<>
			<List dense disablePadding>
				{payments
					.sort((p1, p2) => ZonedDateTime.compare(parseZoneDateTimeFromInstant(p1.createdAt, zoneId), parseZoneDateTimeFromInstant(p2.createdAt, zoneId)))
					.map(({uuid, paidAt, paymentId, amount, createdAt, receiptText}) => (
						<ListItem key={uuid} style={{ padding: 0 }}>
							<ListItemAvatar>
								<Avatar
									sx={theme => ({
										background: notBlank(paidAt) ? green[500] : theme.palette.grey[200],
										color: notBlank(paidAt) ? '#fff' : theme.palette.grey[500],
									})}
								>
									{paymentId ? <CreditCardIcon /> : <ReceiptIcon />}
								</Avatar>
							</ListItemAvatar>
							<ListItemText
								primary={(
									<span>
									{currency ?? 'EUR'} <DisplayCurrency amount={amount} />
								</span>
								)}
								secondary={<TemporalFormat locale={language} value={parseZoneDateTimeFromInstant(createdAt, zoneId)} />}
							/>
							{/* TODO: Sloppily detecting actual receipt with min length until we have a proper flag for this data. */}
							{(receiptText ?? '').length > 200 && (
								<ListItemSecondaryAction>
									<Button
										variant="contained"
										color="primary"
										size="small"
										onClick={() => setReceipt(receiptText)}
									>
										{t('payment.receipt')}
									</Button>
								</ListItemSecondaryAction>
							)}
						</ListItem>
					))
				}
			</List>
			<Dialog
				open={notBlank(receipt)}
				onClose={() => setReceipt(undefined)}
			>
				<DialogTitle>{t('payment.receipt')}</DialogTitle>
				<DialogContent>
					<pre style={{ fontSize: '0.7rem' }}>{receipt}</pre>
					<Box mt={2} mb={1} display="flex" justifyContent="center">
						<Button
							variant="contained"
							onClick={() => setReceipt(undefined)}
						>
							{t('actions.close')}
						</Button>
					</Box>
				</DialogContent>
			</Dialog>
		</>
	)
}
